import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileUploader } from "@/shared/components";
import { Checkbox, DatePicker, Input, Select, CitySelect } from "@/shared/ui";
import { getFamilyStatusOptions } from "@/shared/utils";
import { getNationalityOptions, CITIZENSHIP_OPTIONS } from "@/features/hr/hiring";
import type { GeneralSection } from "../../../types/personalSheet";
import { editGeneralSchema, type EditGeneralValues } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  general: GeneralSection;
  draftValues?: GeneralSection;
}

function mapGeneralToEditValues(general: GeneralSection): EditGeneralValues {
  return {
    surname: general.surname,
    name: general.name,
    father_name: general.father_name,
    family_status: general.family_status,
    citizenship: general.citizenship,
    nationality: general.nationality,
    city_of_birth_id: general.city_of_birth?.id ?? null,
    city_of_birth_other: general.city_of_birth_other ?? null,
    is_resident: general.is_resident,
    is_student: general.is_student,
    enrollment_start_date: general.enrollment_start_date ?? null,
    enrollment_end_date: general.enrollment_end_date ?? null,
  };
}

const EditGeneralSection = forwardRef<SectionFormHandle, Props>(function EditGeneralSection({ general, draftValues }, ref) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const { t: tApply } = useTranslation("ApplyApplicationPage");
  const { t: tCommon } = useTranslation("Common");
  const locale = (i18n.language as "ru" | "kk" | "en") || "ru";

  const initialValues = useRef<EditGeneralValues>(mapGeneralToEditValues(general));
  const displayGeneral = draftValues ?? general;

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [existingPhotoRemoved, setExistingPhotoRemoved] = useState(false);
  const [enrollmentFile, setEnrollmentFile] = useState<File | null>(null);

  const {
    register,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<EditGeneralValues>({
    resolver: zodResolver(editGeneralSchema),
    mode: "onTouched",
    defaultValues: mapGeneralToEditValues(displayGeneral),
  });

  const familyStatus = watch("family_status");
  const citizenship = watch("citizenship");
  const nationality = watch("nationality");
  const isResident = watch("is_resident");
  const cityOfBirthId = watch("city_of_birth_id");
  const cityOfBirthOther = watch("city_of_birth_other");
  const isStudent = watch("is_student");
  const enrollmentStartDate = watch("enrollment_start_date");
  const enrollmentEndDate = watch("enrollment_end_date");

  const familyStatusOptions = getFamilyStatusOptions(general.gender, tCommon);

  const genderOptions = [
    { value: "male", label: t("personalSheet.genderValues.male") },
    { value: "female", label: t("personalSheet.genderValues.female") },
  ];

  const nationalityOptions = getNationalityOptions(general.gender, tApply);

  const citizenshipOptions = CITIZENSHIP_OPTIONS.map((o) => ({
    value: o.value,
    label: tApply(`GeneralForm.citizenship.${o.labelKey}`),
  }));

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        const current = getValues();
        const initial = initialValues.current;
        const changes: Record<string, unknown> = {};

        if (current.surname !== initial.surname) changes.surname = current.surname;
        if (current.name !== initial.name) changes.name = current.name;
        if (current.father_name !== initial.father_name) changes.father_name = current.father_name;
        if (current.family_status !== initial.family_status) changes.family_status = current.family_status;
        if (current.citizenship !== initial.citizenship) changes.citizenship = current.citizenship;
        if (current.nationality !== initial.nationality) changes.nationality = current.nationality;
        if (current.is_resident !== initial.is_resident) changes.is_resident = current.is_resident;
        if (current.city_of_birth_id !== initial.city_of_birth_id) {
          changes.city_of_birth_id = current.city_of_birth_id;
        }
        if (current.city_of_birth_other !== initial.city_of_birth_other) {
          changes.city_of_birth_other = current.city_of_birth_other;
        }
        if (current.is_student !== initial.is_student) changes.is_student = current.is_student;
        if (current.enrollment_start_date !== initial.enrollment_start_date) {
          changes.enrollment_start_date = current.enrollment_start_date;
        }
        if (current.enrollment_end_date !== initial.enrollment_end_date) {
          changes.enrollment_end_date = current.enrollment_end_date;
        }

        return Object.keys(changes).length > 0 ? changes : null;
      },
      getFiles() {
        const files: Record<string, File> = {};
        if (photoFile) files["kyc_documents.worker_photo_file"] = photoFile;
        if (enrollmentFile) files["kyc_documents.enrollment_verification_file"] = enrollmentFile;
        return Object.keys(files).length > 0 ? files : null;
      },
      async validate() {
        return await trigger();
      },
      isDirty() {
        const current = getValues();
        const initial = initialValues.current;
        return (
          current.surname !== initial.surname ||
          current.name !== initial.name ||
          current.father_name !== initial.father_name ||
          current.family_status !== initial.family_status ||
          current.citizenship !== initial.citizenship ||
          current.nationality !== initial.nationality ||
          current.is_resident !== initial.is_resident ||
          current.city_of_birth_id !== initial.city_of_birth_id ||
          current.city_of_birth_other !== initial.city_of_birth_other ||
          current.is_student !== initial.is_student ||
          current.enrollment_start_date !== initial.enrollment_start_date ||
          current.enrollment_end_date !== initial.enrollment_end_date ||
          photoFile !== null ||
          enrollmentFile !== null
        );
      },
    };
  });

  return (
    <div className="flex flex-col gap-4">
      <FileUploader
        label={t("personalSheet.fields.photo")}
        accept=".jpg,.jpeg,.png,.bmp,.webp,.heic,.heif,.tiff,.tif"
        value={photoFile}
        existingFileUrl={existingPhotoRemoved ? null : displayGeneral.photo_url}
        onChange={(file) => setPhotoFile(file)}
        onRemoveExisting={() => setExistingPhotoRemoved(true)}
      />

      <Input
        label={t("personalSheet.fields.surname")}
        hasRequiredLabel
        placeholder={t("personalSheet.fields.surname")}
        {...register("surname")}
        error={errors.surname?.message ? t(errors.surname.message) : undefined}
      />

      <Input
        label={t("personalSheet.fields.name")}
        hasRequiredLabel
        placeholder={t("personalSheet.fields.name")}
        {...register("name")}
        error={errors.name?.message ? t(errors.name.message) : undefined}
      />

      <Input
        label={t("personalSheet.fields.fatherName")}
        placeholder={t("personalSheet.fields.fatherName")}
        {...register("father_name")}
        error={errors.father_name?.message ? t(errors.father_name.message) : undefined}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label={t("personalSheet.fields.iin")} placeholder="" value={general.iin} disabled />
        <DatePicker
          label={t("personalSheet.fields.dateOfBirth")}
          mode="single"
          locale={locale}
          value={general.date_of_birth ? new Date(general.date_of_birth) : null}
          onChange={() => {}}
          disabled
        />
      </div>

      <Select
        label={t("personalSheet.fields.gender")}
        options={genderOptions}
        value={general.gender}
        onChange={() => {}}
        placeholder=""
        disabled
      />

      <Select
        label={t("personalSheet.fields.familyStatus")}
        hasRequiredLabel
        options={familyStatusOptions}
        value={familyStatus}
        onChange={(v) => setValue("family_status", v ?? "", { shouldValidate: true })}
        placeholder={t("personalSheet.fields.familyStatus")}
        error={errors.family_status?.message ? t(errors.family_status.message) : undefined}
      />

      <CitySelect
        label={t("personalSheet.fields.cityOfBirth")}
        value={cityOfBirthId ?? undefined}
        withOther={true}
        otherValue={cityOfBirthOther ?? ""}
        dataSource="regions"
        onChange={(cityId) => {
          setValue("city_of_birth_id", cityId ?? null, { shouldValidate: true });
          if (cityId !== undefined) {
            setValue("city_of_birth_other", null, { shouldValidate: true });
          }
        }}
        onOtherChange={(v) => {
          setValue("city_of_birth_other", v || null, { shouldValidate: true });
          if (v.trim() !== "") {
            setValue("city_of_birth_id", null, { shouldValidate: true });
          }
        }}
        error={errors.city_of_birth_id?.message ? t(errors.city_of_birth_id.message) : undefined}
        otherError={errors.city_of_birth_other?.message ? t(errors.city_of_birth_other.message) : undefined}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label={t("personalSheet.fields.nationality")}
          hasRequiredLabel
          options={nationalityOptions}
          value={nationality}
          onChange={(v) => setValue("nationality", v ?? "", { shouldValidate: true })}
          placeholder={t("personalSheet.fields.nationality")}
          error={errors.nationality?.message ? t(errors.nationality.message) : undefined}
        />
        <Select
          label={t("personalSheet.fields.citizenship")}
          hasRequiredLabel
          options={citizenshipOptions}
          value={citizenship}
          onChange={(v) => setValue("citizenship", v ?? "", { shouldValidate: true })}
          placeholder={t("personalSheet.fields.citizenship")}
          error={errors.citizenship?.message ? t(errors.citizenship.message) : undefined}
        />
      </div>

      <Checkbox
        state={!isResident ? "checked" : "unchecked"}
        onChange={() => setValue("is_resident", !isResident, { shouldValidate: true })}
        label={tApply("personal.not_resident")}
      />

      <Checkbox
        state={isStudent ? "checked" : "unchecked"}
        onChange={() => {
          const newValue = !isStudent;
          setValue("is_student", newValue, { shouldValidate: true });
          if (!newValue) {
            setValue("enrollment_start_date", null, { shouldValidate: true });
            setValue("enrollment_end_date", null, { shouldValidate: true });
            setEnrollmentFile(null);
          }
        }}
        label={t("personalSheet.fields.isStudent")}
      />

      {isStudent && (
        <>
          <FileUploader
            label={t("personalSheet.fields.enrollmentVerificationFile")}
            hasRequiredLabel
            accept=".pdf,.jpg,.jpeg,.png"
            value={enrollmentFile}
            onChange={(file) => setEnrollmentFile(file)}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DatePicker
              label={t("personalSheet.fields.enrollmentStartDate")}
              hasRequiredLabel
              locale={locale}
              value={enrollmentStartDate ? new Date(enrollmentStartDate) : null}
              onChange={(date) => {
                setValue("enrollment_start_date", date ? date.toISOString().split("T")[0] : null, { shouldValidate: true });
              }}
              error={errors.enrollment_start_date?.message ? t(errors.enrollment_start_date.message) : undefined}
            />
            <DatePicker
              label={t("personalSheet.fields.enrollmentEndDate")}
              hasRequiredLabel
              locale={locale}
              value={enrollmentEndDate ? new Date(enrollmentEndDate) : null}
              minDate={enrollmentStartDate ? new Date(enrollmentStartDate) : undefined}
              onChange={(date) => {
                setValue("enrollment_end_date", date ? date.toISOString().split("T")[0] : null, { shouldValidate: true });
              }}
              error={errors.enrollment_end_date?.message ? t(errors.enrollment_end_date.message) : undefined}
            />
          </div>
        </>
      )}
    </div>
  );
});

export default EditGeneralSection;
